import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, PlusIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import ArrowUpIcon from '../../../../../../packages/ui/icons/arrow-up'
import { getTripStepTimezones } from '../../../api/timezones/get-timezones'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import { getTripBookingGuidelines } from '../../../api/trip-booking-guidelines/get-trip-booking-guidelines'
import { getTripBooking } from '../../../api/trip-bookings/get-trip-booking'
import { getTripProviders } from '../../../api/trip-providers/get-trip-providers'
import { DepartureTimeTripProviderInput } from '../../../lib/types/models/trip-booking/departure-time-trip-provider'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import { BaseFormProps, IdInput, NullableTransformer, Time } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createTimeSchema } from '../../../lib/utils/validations/date.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'
import { getAtTripStep, getDpTripStep, TripStepMode } from './utils'

type Props = BaseFormProps<TripStepTypes.Input, true> & {
  mode: TripStepMode
  className?: string
  tripBookingId?: number
}

const formSchema: ZodType<NullableTransformer<TripStepTypes.Input>> = z
  .object({
    departureDate: createStringSchema('Date de départ'),
    arrivalDate: createStringSchema("Date d'arrivée"),
    departurePlaceId: createNumberSchema('Lieu de départ'),
    arrivalPlaceId: createNumberSchema("Lieu d'arrivée"),
    transportMeanNumber: createStringSchema('N° de vol/train', { nullish: true }),
    carNumber: createStringSchema('N° de voiture', { nullish: true }),
    departureTime: createTimeSchema({ inputName: 'heure de départ' }),
    departureTimeZone: createStringSchema('Fuseau horaire'),
    arrivalTime: createTimeSchema({ inputName: "heure d'arrivée" }),
    arrivalTimeZone: createStringSchema('Fuseau horaire'),
    timeBeforeDepartureForLEC: createTimeSchema({ inputName: 'heure de RDV LEC/AAT' }).nullish(),
    timeBeforeDepartureForStaff: createTimeSchema({
      inputName: 'heure de RDV encadrants'
    }).nullish(),
    timeBeforeDepartureForParents: createTimeSchema({
      inputName: 'heure de RDV des parents'
    }).nullish(),
    timeBeforeDepartureForGroup: createTimeSchema({
      inputName: 'heure de RDV groupe/AAT'
    }).nullish(),
    timeBeforeArrivalForLEC: createTimeSchema({ inputName: 'heure de RDV LEC/AAT' }).nullish(),
    timeBeforeArrivalForParents: createTimeSchema({
      inputName: 'heure de RDV des parents'
    }).nullish(),

    foreignProviderAppointmentTime: createTimeSchema({
      inputName: 'heure de RDV prestataire étranger'
    }).nullish(),

    foreignProviderAppointmentPlaceId: createNumberSchema(
      'Lieu de RDV prestataire étranger'
    ).nullish(),

    departureTimeTripProviders: z
      .array(
        z.object({
          label: createStringSchema('Label'),
          meetingPlace: createStringSchema('Lieu de RDV', { nullish: true }),
          time: createTimeSchema({ inputName: 'heure' }),
          tripProviderId: createNumberSchema('Prestataire', { isInteger: true })
        }),
        {
          message: 'Veuillez indiquer un fournisseur',
          required_error: 'Veuillez indiquer un fournisseur'
        }
      )
      .default([]),
    olGuidelineId: createNumberSchema('Guide OL').nullish(),
    olGuidelineContent: createStringSchema('Contenu guide OL').nullish(),
    tripProviderGuidelineId: createNumberSchema('Guide prestataire').nullish(),
    tripProviderGuidelineContent: createStringSchema('Contenu guide prestataire').nullish()
  })
  .superRefine((arg, ctx) => {
    // check if the departure date is before the arrival date
    if (arg.departureDate && arg.arrivalDate && arg.departureDate > arg.arrivalDate) {
      ctx.addIssue({
        path: ['departureDate'],
        code: z.ZodIssueCode.custom,
        message: "La date de départ ne peut pas être après la date d'arrivée"
      })
    }

    // check if departure time is before arrival time when on same day
    if (arg.departureTime && arg.departureDate && arg.arrivalTime && arg.arrivalDate) {
      const departureTime = DateFormatter.setTime(new Date(arg.departureDate), arg.departureTime)
      const arrivalTime = DateFormatter.setTime(new Date(arg.arrivalDate), arg.arrivalTime)

      if (departureTime > arrivalTime) {
        ctx.addIssue({
          path: ['departureTime'],
          code: z.ZodIssueCode.custom,
          message: "L'heure de départ doit être antérieure à l'heure d'arrivée"
        })
      }
    }
  })

const TripStepForm = ({
  mode,
  className,
  defaultValues,
  tripBookingId,
  ...validationButtonProps
}: Props) => {
  const form = useForm<TripStepTypes.Input>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: tripBookingGuidelines } = useQuery({
    queryKey: ['trip-booking-guidelines'],
    queryFn: () => getTripBookingGuidelines()
  })

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const { data: tripProviders } = useQuery({
    queryKey: ['trip-providers'],
    queryFn: () => getTripProviders()
  })

  const { data: timezones } = useQuery({
    queryKey: ['timezones', 'trip-steps'],
    queryFn: () => getTripStepTimezones()
  })

  const departureTimeTripProviders = form.watch('departureTimeTripProviders') ?? []
  const {
    departureTime,
    arrivalTime,
    departureDate,
    arrivalDate,
    olGuidelineId,
    tripProviderGuidelineId
  } = form.watch()

  const { data: tripBooking } = useQuery({
    queryKey: ['trip-booking', tripBookingId],
    queryFn: () => {
      if (tripBookingId) return getTripBooking(tripBookingId, 'PARTIAL')
      return undefined
    },
    enabled: !!tripBookingId
  })

  const selectedTripProviderIds = departureTimeTripProviders
    .map((departureTimeTripProvider) => departureTimeTripProvider.tripProviderId)
    .filter(Boolean)
    .join(',')
  const selectedTripProviderIdSet = useMemo(
    () =>
      new Set(
        selectedTripProviderIds
          .split(',')
          .filter(Boolean)
          .map((id) => Number(id))
      ),
    [selectedTripProviderIds]
  )

  /** remove the chosen ones */
  const providerOptions = useMemo(
    () => tripProviders?.data.filter((option) => !selectedTripProviderIdSet.has(option.id)),
    [selectedTripProviderIdSet, tripProviders]
  )

  const columns = useMemo<Column<IdInput<Partial<DepartureTimeTripProviderInput>>>[]>(
    () => [
      {
        accessorKey: 'tripProviderId',
        header: 'Prestataires voyages',
        width: '34%',
        cell: ({ row }) => {
          return (
            <FormField
              control={form.control}
              name={`departureTimeTripProviders.${row.index}.tripProviderId`}
              render={({ field }) => (
                <FormItem className={'w-full min-w-0'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      label={'Prestataire'}
                      options={providerOptions?.map((option) => ({
                        label: option.companyName,
                        value: option.id
                      }))}
                      selectedOption={conditionalRender(!!row.original.tripProviderId, {
                        label:
                          tripProviders?.data.find(
                            (tripProvider) => tripProvider.id === row.original.tripProviderId
                          )?.companyName ?? '',
                        value: row.original.tripProviderId
                      })}
                      onChange={(value) => {
                        field.onChange(value)
                        form.setValue(
                          `departureTimeTripProviders.${row.index}.label`,
                          tripProviders?.data.find((tripProvider) => tripProvider.id === value)
                            ?.companyName ?? 'non défini'
                        )
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )
        }
      },
      {
        accessorKey: 'meetingPlace',
        header: 'Lieu de RDV',
        width: '34%',
        cell: ({ row }) => {
          return (
            <FormField
              control={form.control}
              name={`departureTimeTripProviders.${row.index}.meetingPlace`}
              render={({ field }) => (
                <FormItem className={'w-full min-w-0'}>
                  <FormControl>
                    <ControlledInput {...field} label={'Lieu de RDV'} type="text" />
                  </FormControl>
                </FormItem>
              )}
            />
          )
        }
      },
      {
        accessorKey: 'time',
        header: 'Heure',
        width: '24%',
        cell: ({ row }) => {
          return (
            <FormField
              control={form.control}
              name={`departureTimeTripProviders.${row.index}.time`}
              render={({ field }) => (
                <FormItem className={'w-full min-w-0'}>
                  <FormControl>
                    <ControlledHourInput {...field} required />
                  </FormControl>
                </FormItem>
              )}
            />
          )
        }
      },
      {
        accessorKey: 'id',
        header: '',
        width: '8%',
        cell: ({ row }) => (
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              const index = row.index
              const providers = form.getValues('departureTimeTripProviders') ?? []

              form.setValue(
                'departureTimeTripProviders',
                providers.filter((_, i) => i !== index)
              )
            }}
          />
        )
      }
    ],
    [form, providerOptions, tripProviders]
  )

  // Each effect recomputes only the times derived from its own source time, so changing the
  // arrival time never resets a convocation time typed after the departure time (and vice versa)
  useEffect(() => {
    if (!tripBooking || tripBooking.isTakeInCharge || !departureTime) return

    const parentsTime = tripBooking.transportMeans.howManyQuarterBeforeDepartureForParents
    const staffTime = tripBooking.transportMeans.howManyQuarterBeforeDepartureForStaff
    const lecTime = tripBooking.transportMeans.howManyQuarterBeforeDepartureForLEC
    const groupTime = tripBooking.transportMeans.howManyQuarterBeforeDepartureForGroup

    if (mode === 'departure') {
      if (parentsTime !== null && parentsTime !== undefined)
        form.setValue(
          'timeBeforeDepartureForParents',
          DateFormatter.manipulateDateToNearestQuarter(departureTime, parentsTime, 'subtract')
        )

      if (staffTime !== null && staffTime !== undefined)
        form.setValue(
          'timeBeforeDepartureForStaff',
          DateFormatter.manipulateDateToNearestQuarter(departureTime, staffTime, 'subtract')
        )

      if (lecTime !== null && lecTime !== undefined)
        form.setValue(
          'timeBeforeDepartureForLEC',
          DateFormatter.manipulateDateToNearestQuarter(departureTime, lecTime, 'subtract')
        )
    }

    if (mode === 'arrival' && groupTime !== null && groupTime !== undefined)
      form.setValue(
        'timeBeforeDepartureForGroup',
        DateFormatter.manipulateDateToNearestQuarter(departureTime, groupTime, 'subtract')
      )
  }, [departureTime, form, mode, tripBooking])

  useEffect(() => {
    if (!tripBooking || tripBooking.isTakeInCharge || !arrivalTime) return

    const arrivalTimeLEC = tripBooking.transportMeans.howManyQuarterBeforeArrivalForLEC
    const arrivalTimeParents = tripBooking.transportMeans.howManyQuarterBeforeArrivalForParents
    const afterProviderArrival =
      tripBooking.transportMeans.howManyQuarterAfterArrivalForForeignProvider

    if (mode === 'arrival') {
      if (arrivalTimeLEC !== null && arrivalTimeLEC !== undefined)
        form.setValue(
          'timeBeforeArrivalForLEC',
          DateFormatter.manipulateDateToNearestQuarter(arrivalTime, arrivalTimeLEC, 'subtract')
        )

      if (arrivalTimeParents !== null && arrivalTimeParents !== undefined)
        form.setValue(
          'timeBeforeArrivalForParents',
          DateFormatter.manipulateDateToNearestQuarter(arrivalTime, arrivalTimeParents, 'subtract')
        )
    }

    if (afterProviderArrival !== null && afterProviderArrival !== undefined)
      form.setValue(
        'foreignProviderAppointmentTime',
        DateFormatter.manipulateDateToNearestQuarter(arrivalTime, afterProviderArrival, 'add')
      )
  }, [arrivalTime, form, mode, tripBooking])

  useEffect(() => {
    if (departureDate && arrivalDate && departureDate > arrivalDate) {
      form.setError('departureDate', {
        message: "La date de départ ne peut pas être après la date d'arrivée"
      })
    }
  }, [departureDate, arrivalDate])

  useEffect(() => {
    if (departureTimeTripProviders && departureTimeTripProviders.length > 0)
      form.clearErrors('departureTimeTripProviders')
  }, [departureTimeTripProviders])

  useEffect(() => {
    if (!olGuidelineId) return
    form.trigger(['olGuidelineContent'])
  }, [olGuidelineId])

  useEffect(() => {
    if (!tripProviderGuidelineId) return
    form.trigger(['tripProviderGuidelineContent'])
  }, [tripProviderGuidelineId])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <div className="flex gap-3">
            <FormField
              control={form.control}
              name={'departureDate'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      required
                      disabledPastDates
                      type={'appointment'}
                      label={'Date de départ'}
                      onChange={(value) => {
                        const nextValue = value ?? ''
                        field.onChange(nextValue)
                        if (!arrivalDate) {
                          form.setValue('arrivalDate', nextValue, { shouldValidate: true })
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'arrivalDate'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      required
                      disabledPastDates
                      type={'appointment'}
                      label={"Date d'arrivée"}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-3">
            <FormField
              control={form.control}
              name={'departurePlaceId'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      searchable
                      label={'Lieu de départ'}
                      options={tripAddresses?.data.map((tripAddress) => ({
                        label: tripAddress.label,
                        value: tripAddress.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripAddresses?.data.find((tripAddress) => tripAddress.id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'arrivalPlaceId'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      searchable
                      label={"Lieu d'arrivée"}
                      options={tripAddresses?.data.map((tripAddress) => ({
                        label: tripAddress.label,
                        value: tripAddress.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripAddresses?.data.find((tripAddress) => tripAddress.id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-3">
            <FormField
              control={form.control}
              name={'transportMeanNumber'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} label={'N° de vol/train'} type="text" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'carNumber'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} label={'N° de voiture'} type="text" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div>
            <div className="h-1" />
            <Badge text="Heure de départ" variant="primary" />
            <div className="h-2" />
            <div className="flex gap-3">
              <FormField
                control={form.control}
                name={'departureTime'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput {...field} required />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'departureTimeZone'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        searchable
                        label={'Fuseau horaire'}
                        options={timezones?.map((timezone) => ({
                          label: timezone.name,
                          value: timezone.name
                        }))}
                        selectedOption={conditionalRender(!!field.value, {
                          label:
                            timezones?.find((timezone) => timezone.name === field.value)?.name ??
                            field.value,
                          value: field.value
                        })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {getDpTripStep(mode).map((step) => (
            <div key={step.name} className="flex gap-3 items-center">
              <div className="flex-1 flex items-center justify-between">
                <Badge text={step.label} variant="warning" />
                <div className="w-10">
                  <ArrowUpIcon
                    color={tailwindColors.grey.light.active}
                    width="24px"
                    height="24px"
                    className="rotate-90"
                  />
                </div>
              </div>
              <FormField
                control={form.control}
                name={step.name}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput
                        {...field}
                        onChange={field.onChange}
                        value={field.value as Time}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          ))}
          {mode === 'arrival' && (
            <FormField
              control={form.control}
              name={'foreignProviderAppointmentPlaceId'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      searchable
                      label={'Lieu de RDV du prestataire étranger'}
                      options={tripAddresses?.data.map((tripAddress) => ({
                        label: tripAddress.label,
                        value: tripAddress.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripAddresses?.data.find((tripAddress) => tripAddress.id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          <div>
            <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
              Heure(s) de RDV des prestataires
            </Typography>
            <div className="h-2" />
            <DataTable<IdInput<Partial<DepartureTimeTripProviderInput>>>
              tableClassName="max-w-full !rounded-3xl"
              data={departureTimeTripProviders.map((departureTimeTripProvider, index) => ({
                ...departureTimeTripProvider,
                id: index
              }))}
              rowCount={departureTimeTripProviders.length}
              paginated={false}
              columns={columns}
              disableHover
              displayEmptyState={false}
              footer={
                <Button
                  variant={'outline'}
                  icon={PlusIcon}
                  disabled={
                    departureTimeTripProviders.some(
                      (departureTimeTripProvider) =>
                        !departureTimeTripProvider.tripProviderId ||
                        !departureTimeTripProvider.time
                    ) ||
                    !providerOptions ||
                    providerOptions?.length === 0
                  }
                  label={'Ajouter prestataire'}
                  type={'button'}
                  className={'border-none'}
                  onClick={() =>
                    form.setValue('departureTimeTripProviders', [
                      ...departureTimeTripProviders,
                      {}
                    ])
                  }
                />
              }
            />

            <div className={'h-2'} />

            <Typography variant={'small'} color={tailwindColors.red.DEFAULT}>
              {form.formState.errors.departureTimeTripProviders?.message ??
                form.formState.errors.departureTimeTripProviders?.root?.message}
            </Typography>
          </div>
          <div>
            <div className="h-1" />
            <Badge text="Heure d'arrivée" variant="primary" />
            <div className="h-2" />
            <div className="flex gap-3">
              <FormField
                control={form.control}
                name={'arrivalTime'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput {...field} required />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'arrivalTimeZone'}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        required
                        searchable
                        label={'Fuseau horaire'}
                        options={timezones?.map((timezone) => ({
                          label: timezone.name,
                          value: timezone.name
                        }))}
                        selectedOption={conditionalRender(!!field.value, {
                          label:
                            timezones?.find((timezone) => timezone.name === field.value)?.name ??
                            field.value,
                          value: field.value
                        })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          {getAtTripStep(mode).map((step) => (
            <div key={step.name} className="flex gap-3 items-center">
              <div className="flex-1 flex items-center justify-between">
                <Badge text={step.label} variant="warning" />
                <div className="w-10">
                  <ArrowUpIcon
                    color={tailwindColors.grey.light.active}
                    width="24px"
                    height="24px"
                    className="rotate-90"
                  />
                </div>
              </div>
              <FormField
                control={form.control}
                name={step.name}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledHourInput
                        {...field}
                        onChange={field.onChange}
                        value={field.value as Time}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          ))}
          {mode === 'departure' && (
            <FormField
              control={form.control}
              name={'foreignProviderAppointmentPlaceId'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      searchable
                      label={'Lieu de RDV du prestataire étranger'}
                      options={tripAddresses?.data.map((tripAddress) => ({
                        label: tripAddress.label,
                        value: tripAddress.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripAddresses?.data.find((tripAddress) => tripAddress.id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}
          <div className="flex flex-col gap-3">
            <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
              Consigne à la compagnie
            </Typography>
            <FormField
              control={form.control}
              name={`olGuidelineId`}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      searchable
                      label={'Type de consigne'}
                      options={tripBookingGuidelines?.data.map((tripBookingGuideline) => ({
                        label: tripBookingGuideline.label,
                        value: tripBookingGuideline.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripBookingGuidelines?.data.find(
                            (tripBookingGuideline) => tripBookingGuideline.id === field.value
                          )?.label ?? '',
                        value: field.value
                      })}
                      onChange={(value) => {
                        field.onChange(value)
                        if (!value) {
                          form.setValue('olGuidelineContent', null)
                          return
                        }
                        form.setValue(
                          'olGuidelineContent',
                          tripBookingGuidelines?.data.find(({ id }) => id === value)?.description
                        )
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'olGuidelineContent'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      disabled={!form.watch('olGuidelineId')}
                      type={'textarea'}
                      label={'Consignes à la compagnie '}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col mt-1 gap-3">
            <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
              {mode === 'departure'
                ? "Consignes à l'arrivée pour le prestataire à l'étranger"
                : "Consignes au départ pour le prestataire à l'étranger"}
            </Typography>
            <FormField
              control={form.control}
              name={`tripProviderGuidelineId`}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      searchable
                      label={'Type de consigne'}
                      options={tripBookingGuidelines?.data.map((tripBookingGuideline) => ({
                        label: tripBookingGuideline.label,
                        value: tripBookingGuideline.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          tripBookingGuidelines?.data.find(({ id }) => id === field.value)
                            ?.label ?? '',
                        value: field.value
                      })}
                      onChange={(value) => {
                        field.onChange(value)
                        if (!value) {
                          form.setValue('tripProviderGuidelineContent', null)
                          return
                        }
                        form.setValue(
                          'tripProviderGuidelineContent',
                          tripBookingGuidelines?.data.find(({ id }) => id === value)?.description
                        )
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'tripProviderGuidelineContent'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      disabled={!form.watch('tripProviderGuidelineId')}
                      type={'textarea'}
                      label={
                        mode === 'departure'
                          ? "Consignes à l'arrivée pour le prestataire à l'étranger"
                          : "Consignes au départ pour le prestataire à l'étranger"
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <ValidationButtons hasBorderTop {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default TripStepForm
